import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../utils/api";
import { formatDateFR } from "../utils/format";

const SCORE_CONFIG = {
  0: { label: "0", color: "#dc2626", bg: "#fee2e2", title: "Non conforme" },
  1: { label: "1", color: "#d97706", bg: "#fef3c7", title: "Partiel" },
  2: { label: "2", color: "#16a34a", bg: "#dcfce7", title: "Conforme" },
};

export default function ChecklistForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [localScores, setLocalScores] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    api.getVisitDetail(id).then(d => {
      setDetail(d);
      const init = {};
      d.active_sections.forEach(sec => {
        sec.checkpoints.forEach(cp => {
          if (cp.score !== null && cp.score !== undefined) {
            init[cp.checkpoint_id] = { score: cp.score, comment: cp.comment || "" };
          }
        });
      });
      setLocalScores(init);
    });
  }, [id]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 2500); }
  function setScore(cpId, score) {
    setLocalScores(p => ({ ...p, [cpId]: { ...p[cpId], score, comment: p[cpId]?.comment || "" } }));
  }
  function setComment(cpId, comment) {
    setLocalScores(p => ({ ...p, [cpId]: { ...p[cpId], comment, score: p[cpId]?.score ?? null } }));
  }

  async function saveCurrent() {
    if (!detail) return;
    const sec = detail.active_sections[currentIdx];
    if (!sec) return;
    const toSave = sec.checkpoints
      .filter(cp => localScores[cp.checkpoint_id]?.score !== undefined && localScores[cp.checkpoint_id]?.score !== null)
      .map(cp => ({
        checkpoint_id: cp.checkpoint_id,
        score: localScores[cp.checkpoint_id].score,
        comment: localScores[cp.checkpoint_id].comment || null
      }));
    if (toSave.length === 0) return;
    setSaving(true);
    try {
      await api.upsertScores(id, toSave);
      showToast("Section sauvegardee");
    } finally { setSaving(false); }
  }

  async function saveAll() {
    const toSave = Object.entries(localScores)
      .filter(([, v]) => v.score !== undefined && v.score !== null)
      .map(([cpId, v]) => ({ checkpoint_id: parseInt(cpId), score: v.score, comment: v.comment || null }));
    setSaving(true);
    try {
      await api.upsertScores(id, toSave);
      showToast("Tout sauvegarde !");
    } finally { setSaving(false); }
  }

  async function goNext() {
    await saveCurrent();
    const sections = detail?.active_sections || [];
    if (currentIdx < sections.length - 1) setCurrentIdx(i => i + 1);
    else navigate(`/visits/${id}/actions`);
  }

  async function goPrev() {
    await saveCurrent();
    if (currentIdx > 0) setCurrentIdx(i => i - 1);
  }

  function getSectionScore(sec) {
    return sec.checkpoints.reduce((acc, cp) => acc + (localScores[cp.checkpoint_id]?.score ?? 0), 0);
  }

  function getSectionCompletion(sec) {
    return sec.checkpoints.filter(cp =>
      localScores[cp.checkpoint_id]?.score !== undefined &&
      localScores[cp.checkpoint_id]?.score !== null
    ).length;
  }

  function getScoreInterpretation(score, max) {
    const pct = max > 0 ? (score / max) * 100 : 0;
    if (pct >= 90) return { label: "Excellent", color: "#16a34a", bg: "#dcfce7" };
    if (pct >= 80) return { label: "Bon", color: "#2563eb", bg: "#dbeafe" };
    if (pct >= 65) return { label: "Moyen", color: "#d97706", bg: "#fef3c7" };
    if (pct >= 50) return { label: "Insuffisant", color: "#dc2626", bg: "#fee2e2" };
    return { label: "Critique", color: "#7f1d1d", bg: "#fecaca" };
  }

  if (!detail) return (
    <div className="empty-state"><div className="empty-state-icon">📋</div><h3>Chargement...</h3></div>
  );

  const sections = detail.active_sections;
  const archivedSections = detail.archived_sections;
  const sec = sections[currentIdx];
  const totalScoreNow = sections.reduce((acc, s) => acc + getSectionScore(s), 0);
  const maxTotal = detail.max_total;
  const interp = getScoreInterpretation(totalScoreNow, maxTotal);

  return (
    <div>
      {/* Header */}
      <div className="page-header-row page-header">
        <div>
          <h1>Grille de controle</h1>
          <p>{detail.store_name} · {detail.visit_date ? formatDateFR(detail.visit_date) : "Date non definie"}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{
            background: interp.bg, border: `1.5px solid ${interp.color}`,
            borderRadius: 10, padding: "6px 14px", display: "flex", alignItems: "center", gap: 8
          }}>
            <span style={{ fontWeight: 800, color: interp.color, fontSize: 18 }}>{totalScoreNow}/{maxTotal}</span>
            <span style={{ fontWeight: 600, color: interp.color, fontSize: 12 }}>{interp.label}</span>
          </div>
          <button className="btn btn-outline btn-sm" onClick={saveAll} disabled={saving}>
            {saving ? "..." : "💾 Tout sauver"}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {Object.entries(SCORE_CONFIG).map(([s, cfg]) => (
          <div key={s} style={{
            display: "flex", alignItems: "center", gap: 6,
            background: cfg.bg, border: `1px solid ${cfg.color}`,
            borderRadius: 8, padding: "4px 12px", fontSize: 12
          }}>
            <span style={{ fontWeight: 800, color: cfg.color, fontSize: 14 }}>{cfg.label}</span>
            <span style={{ color: cfg.color, fontWeight: 500 }}>= {cfg.title}</span>
          </div>
        ))}
      </div>

      {/* Section tabs */}
      <div className="step-nav" style={{ marginBottom: 16 }}>
        {sections.map((s, i) => {
          const done = getSectionCompletion(s) === s.checkpoints.length;
          const active = i === currentIdx;
          return (
            <button key={s.code + i}
              className={`step-pill ${active ? "active" : done ? "done" : ""}`}
              onClick={() => { saveCurrent(); setCurrentIdx(i); }}>
              {s.code}. {s.title.split(" ")[0]}
              {done && !active && " ✓"}
            </button>
          );
        })}
      </div>

      {/* Current section */}
      {sec && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {/* Section header */}
          <div style={{
            background: "var(--navy)", color: "white", padding: "12px 16px",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8
          }}>
            <h3 style={{ fontSize: 14, fontWeight: 700 }}>{sec.code}. {sec.title}</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, color: "rgba(255,255,255,.6)" }}>
                {getSectionCompletion(sec)}/{sec.checkpoints.length} notes
              </span>
              <span style={{
                background: "rgba(232,184,75,.2)", color: "var(--accent)",
                fontSize: 12, fontWeight: 700, padding: "2px 10px", borderRadius: 20
              }}>
                {getSectionScore(sec)}/{sec.max_score} pts
              </span>
            </div>
          </div>

          {/* Column headers */}
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 120px",
            padding: "8px 16px", background: "#f8fafc",
            borderBottom: "1.5px solid var(--border)", gap: 12
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em" }}>
              Point de controle
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", textAlign: "center" }}>
              Note
            </div>
          </div>

          {/* Checkpoints */}
          {sec.checkpoints.map((cp, idx) => {
            const current = localScores[cp.checkpoint_id];
            const scoreVal = current?.score;
            const scored = scoreVal !== undefined && scoreVal !== null;
            const cfg = scored ? SCORE_CONFIG[scoreVal] : null;

            return (
              <div key={cp.checkpoint_id} style={{
                background: idx % 2 === 0 ? "white" : "#fafbfd",
                borderBottom: "1px solid var(--border)",
              }}>
                {/* Main row: description + score buttons */}
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 120px",
                  padding: "14px 16px", gap: 12, alignItems: "center"
                }}>
                  {/* Description */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span style={{
                      background: cfg ? cfg.bg : "var(--bg)",
                      color: cfg ? cfg.color : "var(--muted)",
                      border: `1.5px solid ${cfg ? cfg.color : "var(--border)"}`,
                      borderRadius: 6, padding: "1px 8px", fontSize: 11, fontWeight: 700,
                      flexShrink: 0, minWidth: 28, textAlign: "center"
                    }}>
                      {sec.code}{idx + 1}
                    </span>
                    <span style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.5, wordBreak: "break-word" }}>
                      {cp.description}
                    </span>
                  </div>

                  {/* Score buttons - right aligned */}
                  <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                    {[0, 1, 2].map(s => {
                      const c = SCORE_CONFIG[s];
                      const isActive = scoreVal === s;
                      return (
                        <button key={s} type="button"
                          onClick={() => setScore(cp.checkpoint_id, s)}
                          style={{
                            width: 34, height: 34, borderRadius: 8,
                            border: `2px solid ${isActive ? c.color : "var(--border)"}`,
                            background: isActive ? c.bg : "white",
                            color: isActive ? c.color : "var(--muted)",
                            fontWeight: 800, fontSize: 15, cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            transition: "all .15s", flexShrink: 0,
                          }}>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Comment row */}
                <div style={{ padding: "0 16px 12px", paddingLeft: 54 }}>
                  <input
                    placeholder="Commentaire / anomalie..."
                    value={current?.comment || ""}
                    onChange={e => setComment(cp.checkpoint_id, e.target.value)}
                    style={{
                      width: "100%", fontSize: 12, padding: "7px 10px",
                      border: "1px solid var(--border)", borderRadius: 6,
                      background: current?.comment ? "#fffbeb" : "var(--bg)",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Navigation */}
      <div className="step-actions" style={{ marginTop: 16 }}>
        <button className="btn btn-outline" onClick={goPrev} disabled={currentIdx === 0 || saving}>
          ← Precedent
        </button>
        <div className="flex gap-2">
          <button className="btn btn-outline btn-sm" onClick={saveCurrent} disabled={saving}>
            {saving ? "..." : "💾 Sauvegarder"}
          </button>
          <button className="btn btn-primary" onClick={goNext} disabled={saving}>
            {currentIdx < sections.length - 1 ? "Suivant →" : "Plan d'actions →"}
          </button>
        </div>
      </div>

      {/* Archived sections */}
      {archivedSections.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{
            background: "#fef3c7", border: "1px solid var(--orange)",
            borderRadius: 8, padding: "10px 16px", marginBottom: 16, fontSize: 13
          }}>
            Rubriques archivees — donnees preservees en lecture seule
          </div>
          {archivedSections.map((sec, si) => (
            <div key={si} className="card" style={{ padding: 0, overflow: "hidden", marginBottom: 12 }}>
              <div style={{ background: "var(--navy)", color: "white", padding: "10px 16px", display: "flex", justifyContent: "space-between" }}>
                <h3 style={{ fontSize: 13 }}>{sec.code}. {sec.title} <span style={{ fontSize: 10, opacity: .6 }}>archive</span></h3>
                <span style={{ fontSize: 12, color: "var(--accent)", fontWeight: 700 }}>{sec.obtained}/{sec.max_score} pts</span>
              </div>
              {sec.checkpoints.map((cp, ci) => {
                const cfg = cp.score !== null ? SCORE_CONFIG[cp.score] : null;
                return (
                  <div key={ci} style={{
                    display: "grid", gridTemplateColumns: "1fr 120px",
                    padding: "12px 16px", gap: 12, alignItems: "center",
                    borderBottom: "1px solid var(--border)", background: ci % 2 === 0 ? "white" : "#fafbfd",
                    opacity: 0.85
                  }}>
                    <span style={{ fontSize: 13, color: "var(--text)" }}>{cp.description}</span>
                    <div style={{ display: "flex", justifyContent: "center", gap: 6 }}>
                      {[0, 1, 2].map(s => {
                        const c = SCORE_CONFIG[s];
                        const isActive = cp.score === s;
                        return (
                          <div key={s} style={{
                            width: 34, height: 34, borderRadius: 8,
                            border: `2px solid ${isActive ? c.color : "var(--border)"}`,
                            background: isActive ? c.bg : "white",
                            color: isActive ? c.color : "var(--muted)",
                            fontWeight: 800, fontSize: 15,
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {s}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
